import React, {useEffect, useState} from 'react';
import {checkNullArr, checkNullStr, getArrIndexValue, getJsonValue, isBoolTrue} from "../../common/fusion-ui/utils/ReactUtils";
import {DefStrNA, DefStrNAPlain} from "../../common/fusion-ui/config/FusionConfig";
import {printError, printLog} from "../../common/CommonUtils";
import {sendAdminRequest} from "../utils/AdminUtils";
import {AdminReqUrls} from "../config/AdminConfig";
import {SearchableSelect} from "../../common/fusion-ui";
import {useTheme} from "../../common/ProjectThemeProvider";

/**
 * TimezoneSelect is a memoized React component that provides a searchable select dropdown
 * for selecting timezones. It fetches timezone data on mount and handles changes.
 *
 * @param {string} wrapperClassName - Custom CSS class for the wrapper element, default is an empty string.
 * @param {string} className - Custom CSS class for the select element, default is an empty string.
 * @param {string} defTsId - Default timezone ID to be selected initially.
 * @param {string} label - The label for the select field, default is "Timezone".
 * @param {boolean} required - Boolean indicating if the select field is required.
 * @param {function} onChange - Callback function to be invoked when a timezone is selected.
 *
 * @returns {JSX.Element} A React element that renders a searchable dropdown for timezones.
 *
 * @example
 *   <TimezoneSelect
 *       wrapperClassName={''}
 *       className={''}
 *       required={true}
 *       defTsId={timezoneId}
 *       onChange={(name, id) => {
 *           log('ts.onChange.name:', name, 'id:', id);
 *           setTimezoneName(mf.timezoneName, name);
 *           setTimezoneId(mf.timezoneId, id);
 *           panelLog('');
 *       }}
 * />
 */
const TimezoneSelect = React.memo(({
                                       wrapperClassName = "",
                                       className = "",
                                       defTsId,
                                       label = 'Timezone',
                                       required,
                                       onChange,
                                   }) => {
    const TAG = "TimezoneSelect";

    const theme = useTheme();

    const Fields = {
        isDataLoading: 'isDataLoading',
        isDataFound: 'isDataFound',
        dataLoadingMsg: 'dataLoadingMsg',
        isDataFetched: 'isDataFetched',
    };
    const [fieldsData, setFieldsData] = useState({});
    const [timezoneData, setTimezoneData] = useState({list: [], current: null});
    useEffect(() => {
        // log('useEffect.defTsId:', defTsId);
        fetchTimezones(defTsId);
    }, []);

    return (<>
            <SearchableSelect
                label={label}
                labelAsPlaceholder={true}
                wrapperClassName={`fd-column pt-2 pb-3 ${wrapperClassName}`}
                className={`w-100 m-0 mt-0 ${className}`}
                style={{height: '24px !important'}}
                multiple={false}
                searchable={true}
                required={required}
                justifyAt={'start'}
                labelClassName={'fw-bold fs-sm text-start w-100'}
                labelColor={theme.text40}
                options={timezoneData.list}
                disabled={!checkNullArr(timezoneData.list.filter(item => checkNullStr(getJsonValue(item, 'label'))
                    && getJsonValue(item, 'label') != DefStrNA
                    && getJsonValue(item, 'label') != DefStrNAPlain))}
                dropdownHeight={'100px'}
                defOption={timezoneData.current}
                onChange={(data, label, value) => {
                    // log('tsSelect.onChange.data:', data, 'label:', label, 'value:', value);
                    setTimezoneData(prevState => ({...prevState, current: data}));
                    if (onChange) onChange(label, value);
                }}
            />
        </>
    );

    async function fetchTimezones(defTsId, checkBefore = true) {
        const fun = 'fetchTimezones:';
        if (!checkBefore || (checkBefore && !isFieldTrue(Fields.isDataFetched))) {
            updateField(Fields.isDataFetched, true);
            updateField(Fields.isDataFound, false);
            updateField(Fields.dataLoadingMsg, null);
            setTimezoneData({list: [], current: {}});
            try {
                //send-request
                const REQUEST_OPTIONS = {};
                sendAdminRequest({
                    reqUrl: AdminReqUrls.fetchTimezones,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        // log(fun, 'fetchedData:', fetchedData);
                        let list = getJsonValue(fetchedData, 'data', []);
                        const success = checkNullArr(list);
                        updateField(Fields.isDataFound, success);
                        if (success) {
                            list = list.map(item => ({value: item.id, label: item.name}));
                            setTimezoneData(prevState => ({...prevState, list: list}));
                            if (checkNullStr(defTsId)) {
                                setTimezoneData(prevState => ({...prevState, current: getFormattedTsValue(defTsId, list)}));
                            }
                        }
                        updateField(Fields.isDataLoading, false);
                    }),
                    onError: ((error) => {
                        updateField(Fields.isDataLoading, false);
                        logErr(fun, error);
                    }),
                });
            } catch (error) {
                updateField(Fields.isDataLoading, false);
                logErr(fun, error);
            }
        }
    }

    function getFormattedTsValue(tsId, tsList = timezoneData.list) {
        const fun = 'getFormattedTsValue:';
        let value = {};
        try {
            // log(fun, 'tsId:', tsId, 'tsList:', tsList);
            if (checkNullArr(tsList)) {
                let filteredItems = tsList.filter(item => (item.value == tsId));//item: {value, label}
                value = getArrIndexValue(filteredItems, 0);
                value = {value: getJsonValue(value, 'value'), label: getJsonValue(value, 'label')};
                // log(fun, 'tsId:', tsId, 'value:', value);
            }
        } catch (e) {
            logErr(fun, e)
        }
        return value;
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }
});
export default TimezoneSelect;
